import { useState } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import helpContent from '@/help/helpContent';

/**
 * Caja de ayuda de una sección.
 *
 * El texto puede venir de dos lados:
 *   1. Escrito en la propia pantalla (`title` / `description` / `whatItDoesNot`).
 *   2. Del registro central `help/helpContent.js`, buscándolo por `module`.
 *
 * Lo segundo es lo que arregla el fallo del 2026-08-03: seis pantallas ponían
 * `<SectionHelp module="crm_tasks" />` dando por hecho que el componente iba a
 * buscar el texto, y el componente NO lo buscaba. Salía una cajita que decía
 * «¿Cómo funciona ?» y al abrirla estaba vacía. El texto llevaba meses escrito
 * en el registro sin que nadie lo pintara.
 *
 * Lo que se pasa a mano siempre gana sobre el registro.
 */
export default function SectionHelp({ module, title, description, whatItDoesNot }) {
  const { tp } = useLocale();
  const [open, setOpen] = useState(false);
  const delRegistro = (module && helpContent[module]) || {};
  const elTitulo = title || delRegistro.title;
  const elTexto = description || delRegistro.description;
  const loQueNoHace = whatItDoesNot || delRegistro.whatItDoesNot;

  // Sin título no hay ayuda que pintar. Más vale no poner nada que poner una
  // caja que promete ayuda y está hueca.
  if (!elTitulo) return null;

  return (
    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg" data-testid={`help-section-${module}`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        aria-expanded={open} aria-label={`${tp("Ayuda")}: ${tp(elTitulo)}`}>
        <HelpCircle className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
        <span className="text-xs font-medium text-[#475569] flex-1">{tp("¿Cómo funciona")} {tp(elTitulo)}?</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-[#94A3B8]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8]" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-xs text-[#475569] leading-relaxed">
          {elTexto && <p>{tp(elTexto)}</p>}
          {loQueNoHace && (
            <p className="text-[#94A3B8] italic">{tp("Lo que NO hace:")} {tp(loQueNoHace)}</p>
          )}
        </div>
      )}
    </div>
  );
}
